import React, { useEffect, useState } from 'react';
import { Building2, Check, ChevronDown, Save } from 'lucide-react';
import { BrandProfile } from '../types';
import { api } from '../utils/api';
import { useToast } from '../contexts/ToastContext';
import { useTranslation } from '../i18n/translations';

interface BrandProfileSettingsProps {
  profile: BrandProfile;
  onChange: (profile: BrandProfile) => void;
}

export const EMPTY_BRAND_PROFILE: BrandProfile = {
  agencyName: '',
  clientName: '',
  writingTone: 'احترافي مباشر',
  preferredCta: 'تواصل معنا لمعرفة التفاصيل',
  bannedWords: '',
  priceFormat: 'إظهار السعر والمقدم ومدة التقسيط',
  contactInfo: '',
  primaryColor: '#0A3566',
};

export const BrandProfileSettings: React.FC<BrandProfileSettingsProps> = ({ profile, onChange }) => {
  const { tx } = useTranslation();
  const { showToast } = useToast();
  const [draft, setDraft] = useState(profile);
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => setDraft(profile), [profile]);

  const update = (key: keyof BrandProfile, value: string) => setDraft((current) => ({ ...current, [key]: value }));

  const save = async () => {
    setIsSaving(true);
    try {
      const result = await api.saveBrandProfile(draft);
      onChange(result.profile);
      showToast(tx('تم حفظ هوية الوكالة وتطبيقها على الحملات الجديدة'), 'success');
      setIsOpen(false);
    } catch (error: any) {
      showToast(tx(error.message || 'تعذر حفظ هوية الوكالة'), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const completedFields = Object.entries(draft).filter(([, value]) => String(value).trim()).length;

  return (
    <section className="brand-profile-settings">
      <button type="button" className="brand-profile-summary" onClick={() => setIsOpen((value) => !value)} aria-expanded={isOpen}>
        <span className="brand-profile-icon"><Building2 /></span>
        <span className="min-w-0 flex-1 text-start">
          <strong>{tx('هوية الوكالة والعميل')}</strong>
          <small>{draft.agencyName || tx('أضف أسلوب وكالتك وبيانات العميل لتطبيقها تلقائيًا')}</small>
        </span>
        <span className="brand-profile-completion"><Check /> {completedFields}/8</span>
        <ChevronDown className={isOpen ? 'is-rotated' : ''} />
      </button>

      {isOpen && (
        <div className="brand-profile-form animate-fadeIn">
          <div className="brand-profile-form-intro">
            <h3>{tx('ذاكرة الهوية')}</h3>
            <p>{tx('تُحفظ هذه الإعدادات في حسابك وتُرفق بالحملات والتقارير وروابط مراجعة العملاء.')}</p>
          </div>
          <div className="campaign-field-grid">
            <div><label className="field-label">{tx('اسم الوكالة')}</label><input className="field" value={draft.agencyName} onChange={(event) => update('agencyName', event.target.value)} placeholder={tx('مثال: وكالة الهدف للتسويق')} /></div>
            <div><label className="field-label">{tx('اسم العميل الحالي')}</label><input className="field" value={draft.clientName} onChange={(event) => update('clientName', event.target.value)} placeholder={tx('مثال: اسم الشركة أو العلامة')} /></div>
          </div>
          <div className="campaign-field-grid">
            <div><label className="field-label">{tx('نبرة الكتابة')}</label><input className="field" value={draft.writingTone} onChange={(event) => update('writingTone', event.target.value)} /></div>
            <div><label className="field-label">{tx('الدعوة المفضلة للإجراء')}</label><input className="field" value={draft.preferredCta} onChange={(event) => update('preferredCta', event.target.value)} /></div>
          </div>
          <div><label className="field-label">{tx('كلمات لا يجب استخدامها')}</label><input className="field" value={draft.bannedWords} onChange={(event) => update('bannedWords', event.target.value)} placeholder={tx('افصل الكلمات بفاصلة')} /></div>
          <div className="campaign-field-grid">
            <div><label className="field-label">{tx('طريقة عرض السعر')}</label><input className="field" value={draft.priceFormat} onChange={(event) => update('priceFormat', event.target.value)} /></div>
            <div><label className="field-label">{tx('بيانات التواصل')}</label><input className="field" value={draft.contactInfo} onChange={(event) => update('contactInfo', event.target.value)} placeholder={tx('رقم الهاتف أو واتساب أو البريد')} /></div>
          </div>
          <div className="brand-color-field">
            <label className="field-label">{tx('لون هوية العميل')}</label>
            <input type="color" value={draft.primaryColor} onChange={(event) => update('primaryColor', event.target.value)} />
            <input className="field" value={draft.primaryColor} onChange={(event) => update('primaryColor', event.target.value)} dir="ltr" />
          </div>
          <div className="brand-profile-actions">
            <button type="button" className="btn btn-ghost" onClick={() => setIsOpen(false)}>{tx('إلغاء')}</button>
            <button type="button" className="btn btn-primary" onClick={save} disabled={isSaving}>
              {isSaving ? <span className="spinner-soft" /> : <Save />}
              {isSaving ? tx('جاري الحفظ...') : tx('حفظ هوية الوكالة')}
            </button>
          </div>
        </div>
      )}
    </section>
  );
};
